import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from "react-router-dom";
import { fetchETFs } from '../../Api/tickersApi';
import TickerSearch from "../../Components/TickerSearch/TickerSearch";
import PositionCollection from "./Collection/PositionCollection";
import { Tabs, Row, Input, Typography, Space, Segmented, Progress } from 'antd';
import PredefinedGroups from "./PredefinedGroups";
import ForceChart from "../../charts/ForceChart";
import { extractDataForForceChart } from "./utils";
import XrayChord from "../Xray/XrayChord";
import IntersectingPositions from './Intersections/PositionIntersectionTables';
import {debounce} from  'lodash';
import {AddCircleRounded, GridViewRounded, ListAltRounded, RestartAltRounded} from "@mui/icons-material";
import Button from "@mui/material/Button";
import PageHeader from "../../Components/PageHeader";
import {iconMapping} from "../../Configs/AppsConfig";

const { TabPane } = Tabs;


/**
 *
 * @param onReset {function} - A function to call when the reset button is clicked
 * @param dropdownItems {Array[object]} - An array of objects with 'label' and 'callback' properties
 * @returns {JSX.Element}
 * @constructor
 */
const ETFMatrix = ({ onReset, dropdownItems }) => {
    const navigate = useNavigate();
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [filters, setFilters] = useState({
      search: [], sector: []
  });
  const [positionCollectionMode, setPositionCollectionMode] = useState('matrix'); // ['matrix', 'consolidated'
  const [searchParams] = useSearchParams();

  const { positions, status } = useSelector(state => state.tickers.etfs);
  const etfWeights = useSelector(state => state.appStates.etfMatrix.weights);
  const etfColors = useSelector(state => state.appStates.etfColors);
  const dispatch = useDispatch();



  useEffect(() => {
        const etfsParam = searchParams.get('etfs');
        if (etfsParam) {
            const etfsSymbols = etfsParam.split(',').filter(Boolean);
            if (etfsSymbols.length > 0) {
                const etfsToFetch = etfsSymbols.map(symbol => ({ symbol }));
                handleTickersSubmit(etfsToFetch);
            }
        }
    }, [searchParams]);

  useEffect(() => {
      // This effect depends on selectedTickers and positions; ensure it correctly identifies when to fetch new tickers.
      const tickersToFetch = selectedTickers.filter(ticker => !positions.hasOwnProperty(ticker.symbol));
      if (tickersToFetch.length > 0) {
        dispatch(fetchETFs(tickersToFetch.map(t => t.symbol)));
      }
    }, [dispatch, selectedTickers, positions]); // Review dependencies to ensure they trigger fetch as expected


    const handleTickersSubmit = (newTickers) => {
      const uniqueNewTickers = newTickers.filter(newTicker => !selectedTickers.some(ticker => ticker.symbol === newTicker.symbol));
      setSelectedTickers(prevTickers => prevTickers.concat(uniqueNewTickers));
      setShowSearch(false);
    }
  const handleRemoveTicker = (ticker)=>{
    setSelectedTickers(prevTickers => prevTickers.filter(t => t.symbol !== ticker.symbol));

  }

  const handleReset = () => {
    setSelectedTickers([]);

    onReset && onReset();
  };

  const handleAddETFsClick = () => {
      setShowSearch(true); // Open the search popup
  };

  const handleSelectGroup = (etfs) => {
    const etfObjects = etfs.map(etf => ({ symbol: etf.toLowerCase() }));
    handleTickersSubmit(etfObjects);
  };
  const dataForForce = positions ? extractDataForForceChart(positions):null;

  const debouncedUpdateSearch = debounce((newSearch) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      search: newSearch
    }));
  }, 300);

  const handleSearchChange = (e) => {
    const searchValue = e.target.value;
    const searchArray = searchValue.split(',').map(s => s.trim()).filter(Boolean);
    debouncedUpdateSearch(searchArray);
  };

  const handleRankFeatures = ()=>{
      navigate(`/beta/rank-features?symbols=${selectedTickers.map(t=>t.symbol).join(',')}`);
  }

   const filteredPositions = Object.fromEntries(
    Object.entries(positions).filter(([symbol]) =>
      selectedTickers.some(ticker => ticker.symbol === symbol)
    )
  );
   // reduce tickers from filteredPositions and then check for weight in etfWeights
   const totalWeight = Object.values(filteredPositions).reduce((acc, etf) => {
         return acc + (etfWeights[etf.symbol] ?? 0);

   }, 0);

   const actions = [
       // <Button onClick={handleRankFeatures}>Analyze ETFs</Button>,
       <Button onClick={handleAddETFsClick} color="primary" variant={'contained'} startIcon={<AddCircleRounded />}>
           Add ETFs
       </Button>,
       <Button danger onClick={handleReset} color={'error'} variant={'outlined'} startIcon={<RestartAltRounded />}>
            Reset
       </Button>,

  ];

  return (
    <div>
        <PageHeader
          title="ETF Matrix"
          logoUrl={iconMapping.positions}
          actions={actions}
          dropdownItems={dropdownItems}

        />
      {selectedTickers.length === 0 && !showSearch && (
          <div style={{margin: '100px'}}>
            <PredefinedGroups onSelectGroup={handleSelectGroup} />
          </div>
      )}

      {showSearch && (
        <TickerSearch
            onTickersSubmit={handleTickersSubmit}
            closeSearch={()=>{setShowSearch(false);}}
            types={['etf']}
        />
      )}
      {selectedTickers.length > 0 && !showSearch  && (
          <Tabs defaultActiveKey="1" type="card" centered={true} className={'pt-3'}>
              <TabPane tab="Overview" key="1">
                  <Row className={'px-6'} align={'middle'} justify={'space-between'} style={{borderBottom: '2px solid #ededed'}}>
                      <Space direction={'vertical'}>
                          <Space>
                              <Typography.Text style={{fontSize: 12, fontWeight: '600'}}>
                                  Search for multiple stocks below by entering tickers separated by commas.
                              </Typography.Text>
                          </Space>
                          <Input
                              rootClassName={'border'}
                              placeholder="aapl, googl, msft, amzn, tsla, meta, nflx, nvda ..."
                              onChange={handleSearchChange}
                              style={{ marginBottom: 16, width: 600 }}
                            />
                      </Space>
                      <Space>
                          <Typography.Title level={5} style={{marginBottom: 0}}>
                                Total Weight
                          </Typography.Title>
                          <Progress
                            percent={totalWeight * 100} // Cap the progress at 100% to prevent overflow in the UI
                            type={'line'}
                            size={[200, 10]}
                            strokeColor={'#001342'}
                            status={'normal'}

                            format={() => `${(totalWeight * 100).toFixed(1)}%`}
                          />
                      </Space>
                      <Segmented
                          className={'px-3'}
                          options={[
                            {value: "matrix", label: "ETF Matrix", icon: <GridViewRounded/>},
                            {value: 'consolidated', label: "Consolidated Stock List", icon: <ListAltRounded/>}
                          ]}
                          onChange={(value)=>setPositionCollectionMode(value)}
                      />
                  </Row>
                <PositionCollection
                    selectedTickers={selectedTickers}
                    etfWeights={etfWeights}
                    positions={filteredPositions}
                    onRemove={handleRemoveTicker}
                    filters={filters}
                    etfColors={etfColors}
                    mode={positionCollectionMode}
                />
              </TabPane>
              <TabPane tab="Interactive Explorer" key={'2'}>
                  <div>
                      <ForceChart data={dataForForce} etfs={positions} />
                  </div>
              </TabPane>

              <TabPane tab={'Overlap'} key={'3'}>
                  <XrayChord
                    parents={positions}
                  />
              </TabPane>
                <TabPane tab={'Intersections'} key={'5'}>
                    {positions && <IntersectingPositions positions={positions} />}
                </TabPane>
          </Tabs>
      )}
    </div>
  );
};


export default ETFMatrix;